import { CSSProperties } from 'react';
import {
  Autocomplete as MUIAutocomplete,
  AutocompleteProps as MUIAutocompletePropsType,
} from '@mui/material';

import {
  List,
  Progress,
  TextField,
  TextFieldProps as TextFieldPropsType,
} from '@atom/mui';
import fonts from '@atom/styles/fonts';

const { ListItemText } = List;

type AtomMUIInputLabelProps = {
  style?: CSSProperties;
};

type CustomProps = {
  style?: CSSProperties;
  loading?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  TextFieldProps?: TextFieldPropsType;
  InputLabelProps?: AtomMUIInputLabelProps;
};

type AtomMUIProps = {
  id?: string;
  options: any[];
  getOptionLabel?: any;
  renderOption?: any;
  fullWidth?: boolean;
  value: any;
  onChange: any;
  disabled?: boolean;
  filterOptions?: (options: Array<any>, state: object) => Array<any>;
  blurOnSelect?: boolean;
  error?: boolean;
  'data-cy'?: string;
};

export type SelectAutocompleteProps = CustomProps & AtomMUIProps;

const styles = {
  autocomplete: {
    '& .MuiAutocomplete-popupIndicator': {
      padding: '4px',
    },
    '& .MuiAutocomplete-clearIndicator': {
      padding: '4px',
    },
  },
  defaultStyle: {
    width: '100%',
  },
  option: {
    fontSize: fonts.md,
    fontWeight: '500',
  },
};

export const SelectAutocomplete = (props: SelectAutocompleteProps) => {
  const {
    style,
    loading,
    getOptionLabel = (option: string) => option,
    renderOption = (option: any) => option,
    fullWidth = true,
    disabled = false,
    label = '',
    placeholder = 'Search',
    TextFieldProps,
    InputLabelProps = {},
    helperText,
    error,
    ...data
  } = props;

  const MUIAutocompleteProps: Omit<
    MUIAutocompletePropsType<any, any, any, any>,
    'renderInput' | 'autoComplete'
  > = data;

  return (
    <div style={{ ...styles.defaultStyle, ...style }}>
      <MUIAutocomplete
        {...MUIAutocompleteProps}
        sx={styles.autocomplete}
        fullWidth={fullWidth}
        getOptionLabel={getOptionLabel}
        autoHighlight
        disabled={disabled}
        // set disableClearable and freeSolo true when loading
        // to remove internal end adornments
        disableClearable={loading}
        freeSolo={loading}
        renderInput={params => (
          <TextField
            {...params}
            {...TextFieldProps}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            slotProps={{
              htmlInput: params.slotProps.htmlInput,
              inputLabel: {
                ...params.slotProps.inputLabel,
                ...InputLabelProps,
              },
              input: {
                ...(params.slotProps?.input ?? {}),
                ...(loading && {
                  endAdornment: <Progress size={16} />,
                }),
              },
            }}
          />
        )}
        renderOption={(propOptions, option) => {
          const { key, ...optionProps } = propOptions;

          return (
            <li key={key} {...optionProps}>
              <ListItemText>
                <span style={styles.option}>{renderOption(option)}</span>
              </ListItemText>
            </li>
          );
        }}
      />
    </div>
  );
};

export default SelectAutocomplete;
